import moment, { type Moment } from 'moment';
import React, { useEffect, useMemo } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import useTitle from 'hooks/useTitle';
import { useFetchEventByIdQuery } from 'hooks/query/useEventQueries';

import * as routes from 'constants/routes';

import history from 'utils/history';

import EventForm from './EventForm';
import BackButton from 'components/common/button/BackButton';
import Loading from 'components/common/loading/Loading';

type EventRouteParams = {
  id?: string;
};

type EventRecord = {
  id: number;
  name: string;
  description: string | null;
  link: string | null;
  eventDate: Moment;
};

type EventByIdResponse = {
  data: Omit<EventRecord, 'eventDate'> & { eventDate: string };
};

const CreateEditEvent: React.FC<RouteComponentProps<EventRouteParams>> = (props) => {
  const eventId = props.match.params?.id;
  const title = eventId ? 'Edit Event' : 'Add Event';
  const setTitle = useTitle();

  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  const {
    data: response,
    isLoading: loading,
    isError,
  } = useFetchEventByIdQuery<EventByIdResponse>(eventId, Boolean(eventId));

  useEffect(() => {
    if (isError) {
      history.push(routes.EVENTS);
    }
  }, [isError]);

  const event = useMemo<EventRecord | null>(() => {
    if (!response?.data) {
      return null;
    }

    const { data } = response;

    return {
      ...data,
      description: data.description ?? null,
      eventDate: moment(data.eventDate),
      link: data.link ?? null,
    };
  }, [response]);

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div className="d-flex flex-row">
          <BackButton />
          <div className="d-flex flex-row">
            <div className="table-header">
              <h3 className="text-bold">{title}</h3>
            </div>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="loading-min-height">
          <Loading />
        </div>
      ) : (
        <EventForm event={event} />
      )}
    </div>
  );
};

export default CreateEditEvent;
